import {
  useCallback, useEffect, useMemo, useState
} from "react";
import { useDispatch, useSelector } from "react-redux";

import { get, STORAGE_KEYS, save } from "API/LocalStorage";
import { CustomIMergeEntryProps } from "Components/Generic/Merge";
import { projectsStagingMergeStart, uiStagingOpenSet } from "Store/Actions";
import { selectSelectedOrActiveProjects } from "Store/Selectors/Projects";
import { selectStagingValues } from "Store/Selectors/Staging";
import { selectIsStagingOpen } from "Store/Selectors/UI";
import { hasNoElements } from "Utils/Array";
import { ICompany } from "Model/Storage/ICompany";
import { EntityType } from "Model/EntityType";
import { isTrue } from "Utils/Boolean";
import { IProject } from "Model/Storage/IProject";
import { useAuthenticationPreflight } from "Components/Generic/Authentication";
import { isEmpty } from "Utils/Object";
import { IUseStaging } from "./IUseStaging";

type INotMergedProject = {
  "@id": string;
  mergedProject: IProject;
};

export function useStaging(): IUseStaging {
  const dispatch = useDispatch();
  const { preflightAuthentication } = useAuthenticationPreflight();
  const [mergedProject, setMergedProject] = useState<IProject>({} as IProject);

  const isStagingOpen = useSelector(selectIsStagingOpen);
  const stagingValues = useSelector(selectStagingValues);
  const [selectedProject] = useSelector(selectSelectedOrActiveProjects);
  const [selectionMap, setSelectionMap] = useState(new Map<string, "source" | "target">());
  const [notMergedProjects, setNotMergedProjects] = useState<INotMergedProject[]>(
    get<INotMergedProject[]>(STORAGE_KEYS.NOT_MERGED_PROJECTS) ?? []
  );

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    setMergedProject({} as IProject);
    if (isEmpty(selectedProject)) {
      return null;
    }
    const tmpMap = new Map();
    // eslint-disable-next-line consistent-return
    notMergedProjects.forEach((project) => {
      if (project[`@id`] !== selectedProject[`@id`]) {
        return null;
      }
      const fieldNames = Object.keys(project.mergedProject);
      fieldNames.forEach((fieldName) => {
        tmpMap.set(fieldName, `target`);
      });
    });
    setSelectionMap(tmpMap);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    if (isEmpty(selectedProject)) {
      return null;
    }
    const notMergedProjectsWithoutSelectedProject = deleteSelectedProjectFromNotMergedProjects();
    if (mergedProject.companies?.length !== 0 || Object.keys(mergedProject).length !== 1) {
      setNotMergedProjects([
        ...notMergedProjectsWithoutSelectedProject,
        { "@id": selectedProject[`@id`], mergedProject }
      ]);
    } else {
      setNotMergedProjects(notMergedProjectsWithoutSelectedProject);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mergedProject]);

  useEffect(() => {
    save(STORAGE_KEYS.NOT_MERGED_PROJECTS, notMergedProjects);
  }, [notMergedProjects]);

  function deleteSelectedProjectFromNotMergedProjects() {
    return [...notMergedProjects].filter((item) => item[`@id`] !== selectedProject[`@id`]);
  }

  const useExport = useMemo(() => {
    const isExported = isTrue(selectedProject?.[`@isExported`], false);
    const hasNoCompaniesSelected = hasNoElements(mergedProject.companies);
    const hasStagingValuesSelected = Object.keys(mergedProject).filter((key) => key !== `companies`).length > 0;
    return isExported && hasNoCompaniesSelected && hasStagingValuesSelected;
  }, [mergedProject, selectedProject]);

  const onMergeChange = useCallback(
    (key: string, value: unknown) => {
      const [selectedKey, fieldName] = key.split(`.`);
      if (selectionMap.get(fieldName) === selectedKey) {
        return;
      }

      setSelectionMap((previous) => new Map(previous.set(fieldName, selectedKey as "target" | "source")));
      setMergedProject((previous) => {
        const clone = { ...previous, [fieldName]: value ?? null };
        if (selectedKey === `source` && Object.hasOwn(clone, fieldName)) {
          delete clone[fieldName];
        }
        return clone;
      });
    },
    [selectionMap]
  );

  const onDismiss = useCallback(() => {
    dispatch(uiStagingOpenSet(false));
  }, [dispatch]);

  const onSave = useCallback(
    // eslint-disable-next-line consistent-return
    async (withExport = false) => {
      await preflightAuthentication();
      dispatch(projectsStagingMergeStart(mergedProject, true, withExport));
      onDismiss();
      if (isEmpty(selectedProject)) {
        return null;
      }
      const notMergedProjectsWithoutSelectedProject = deleteSelectedProjectFromNotMergedProjects();
      setNotMergedProjects(notMergedProjectsWithoutSelectedProject);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch, mergedProject, onDismiss, preflightAuthentication]
  );

  const onSaveExport = useCallback(() => onSave(true), [onSave]);

  const entries = useMemo(
    () => Object.keys(stagingValues).reduce<CustomIMergeEntryProps[]>((out, fieldName) => {
      if (fieldName.startsWith(`@`) || [`companies`].includes(fieldName)) {
        return out;
      }

      return [
        ...out,
        {
          fieldName,
          entityType: EntityType.Project,
          sourceItem: selectedProject,
          targetItem: stagingValues,
          selectedKey: selectionMap.get(fieldName) ?? `source`,
          allowDisableValue: false
        }
      ];
    }, []),
    [selectedProject, selectionMap, stagingValues]
  );

  useEffect(() => {
    if (hasNoElements(entries)) {
      return;
    }

    setMergedProject((previous) => entries.reduce((out, { fieldName, selectedKey, targetItem }) => {
      if (selectedKey !== `target`) {
        return out;
      }

      return {
        ...out,
        [fieldName]: targetItem?.[fieldName]
      };
    }, previous));
  }, [entries]);

  const newCompanies = stagingValues.companies as ICompany[];

  return {
    entries,
    isStagingOpen,
    newCompanies,
    onDismiss,
    onMergeChange,
    onSave,
    onSaveExport,
    useExport
  };
}
